import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

export default function ClubMembers() {
  const { clubId } = useParams();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchMembers = async () => {
    try {
      const res = await api.get(`/api/club/${clubId}/members`);
      setMembers(res.data?.members || res.data || []);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); }, [clubId]);

  const handleAction = async (userId, action) => {
    setActionLoading(`${userId}-${action}`);
    try {
      await api.post(`/api/club/${clubId}/members/${action}`, { userId });
      await fetchMembers();
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${action} member`);
    }
    setActionLoading(null);
  };

  const statusBadge = (status) => {
    const colors = {
      active: { bg: '#065f46', text: '#10b981' },
      suspended: { bg: '#7f1d1d', text: '#ef4444' },
      pending: { bg: '#92400e', text: '#fbbf24' },
      invited: { bg: '#1e3a5f', text: '#3b82f6' },
      left: { bg: '#374151', text: '#9ca3af' },
    };
    const c = colors[status] || colors.pending;
    return (
      <span style={{
        padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
        background: c.bg, color: c.text, textTransform: 'uppercase'
      }}>{status}</span>
    );
  };

  const roleBadge = (isCaptain) => (
    <span style={{
      padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: isCaptain ? '#92400e' : '#1e3a5f',
      color: isCaptain ? '#fbbf24' : '#93c5fd'
    }}>{isCaptain ? 'Captain' : 'Member'}</span>
  );

  if (loading) return <p style={{ color: '#aaa' }}>Loading members...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ color: '#fff', margin: 0 }}>Manage Members</h2>
        <span style={{ color: '#aaa' }}>{members.length} member{members.length !== 1 ? 's' : ''}</span>
      </div>

      <table className="members-inline-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #3a3d4a' }}>
            {['Member', 'Role', 'Status', 'Joined', 'Contribution', 'Actions'].map(h =>
              <th key={h} style={{ padding: '12px 8px', textAlign: 'left', color: '#aaa', fontSize: 12, textTransform: 'uppercase' }}>{h}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {members.map((m, i) => {
            const name = [m.firstname, m.surname].filter(Boolean).join(' ') || m.email || `User #${m.userId}`;
            const isActive = m.status === 'active';
            const isSuspended = m.status === 'suspended';

            return (
              <tr key={i} style={{ borderBottom: '1px solid #2a2d3a' }}>
                <td style={{ padding: '12px 8px' }}>
                  <div style={{ color: '#fff', fontWeight: 500 }}>{name}</div>
                  <div style={{ color: '#666', fontSize: 12 }}>{m.email}</div>
                </td>
                <td style={{ padding: '12px 8px' }}>{roleBadge(m.isCaptain)}</td>
                <td style={{ padding: '12px 8px' }}>{statusBadge(m.status || 'active')}</td>
                <td style={{ padding: '12px 8px', color: '#aaa', fontSize: 13 }}>
                  {m.joined ? new Date(m.joined).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '12px 8px', color: '#fff' }}>
                  ${m.contributionAmount ? (m.contributionAmount / 100).toFixed(2) : m.currentContributionAmount || '0.00'}
                </td>
                <td style={{ padding: '12px 8px' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {isActive && !m.isCaptain && (
                      <button
                        onClick={() => handleAction(m.userId, 'suspend')}
                        disabled={actionLoading === `${m.userId}-suspend`}
                        style={{ padding: '4px 10px', background: '#7f1d1d', color: '#ef4444', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                      >Suspend</button>
                    )}
                    {isSuspended && (
                      <button
                        onClick={() => handleAction(m.userId, 'activate')}
                        disabled={actionLoading === `${m.userId}-activate`}
                        style={{ padding: '4px 10px', background: '#065f46', color: '#10b981', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                      >Activate</button>
                    )}
                    {!m.isCaptain && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Remove ${name} from the club?`)) {
                            handleAction(m.userId, 'eject');
                          }
                        }}
                        disabled={actionLoading === `${m.userId}-eject`}
                        style={{ padding: '4px 10px', background: '#374151', color: '#9ca3af', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                      >Remove</button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {members.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 30, color: '#666', textAlign: 'center' }}>No members found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
